/**
 * Ring Builder Module
 * Manages the sapphire + setting pairing flow with sessionStorage context
 */

const RingBuilder = (function() {
  const STORAGE_KEY = 'ringBuilderContext';
  const EXPIRY_HOURS = 2;

  /**
   * Show an inline message to the user
   */
  function showRingBuilderMessage(message, type = 'info') {
    // Remove any existing message
    const existingMessage = document.querySelector('.ring-builder-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    // Find the button to insert message near
    const button = document.querySelector('[data-ring-builder="setting"]') ||
                   document.querySelector('[data-ring-builder="sapphire"]');

    if (!button) return;

    // Create message element
    const messageEl = document.createElement('div');
    messageEl.className = `ring-builder-message ring-builder-message--${type}`;
    messageEl.innerHTML = `
      <p>${message}</p>
      <button type="button" class="ring-builder-message__close" aria-label="Close message">×</button>
    `;

    // Insert after button's parent container
    const container = button.closest('.product-form__buttons') || button.parentElement;
    container.insertAdjacentElement('afterend', messageEl);

    // Add close functionality
    messageEl.querySelector('.ring-builder-message__close').addEventListener('click', () => {
      messageEl.remove();
    });
  }

  /**
   * Save context data to sessionStorage
   */
  function saveContext(data) {
    const context = {
      ...data,
      timestamp: Date.now()
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  }

  /**
   * Get context from sessionStorage (returns null if expired or not found)
   */
  function getContext() {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    try {
      const context = JSON.parse(stored);
      const now = Date.now();
      const expiryMs = EXPIRY_HOURS * 60 * 60 * 1000;
      const age = now - context.timestamp;

      if (age > expiryMs) {
        clearContext();
        return null;
      }

      return context;
    } catch (e) {
      clearContext();
      return null;
    }
  }

  /**
   * Clear the ring builder context
   */
  function clearContext() {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Check if ring builder flow is active
   */
  function isActive() {
    return getContext() !== null;
  }

  /**
   * Check if the current Stone product belongs to the expected collection
   * Returns true if validation passes or doesn't apply
   */
  function isStoneInValidCollection() {
    const context = getContext();
    if (!context || !context.sapphireCollectionUrl) return true;

    const productData = document.getElementById('ring-builder-product-data');
    if (!productData || productData.dataset.role !== 'Stone') return true;

    const productCollections = productData.dataset.productCollections;
    if (!productCollections) return false;

    const collectionUrls = productCollections.split(',').map(url => url.trim().toLowerCase());
    const expectedUrl = context.sapphireCollectionUrl.toLowerCase();

    return collectionUrls.some(url => url === expectedUrl || url.endsWith(expectedUrl));
  }

  /**
   * Detect what type of page we're on in relation to ring builder flow
   */
  function detectRingBuilderPage() {
    const body = document.body;
    const context = getContext();
    const productData = document.getElementById('ring-builder-product-data');

    const isProductPage = body.classList.contains('body-template-product');
    const isCollectionPage = body.classList.contains('body-template-collection');
    const isCartPage = body.classList.contains('body-template-cart');

    // Ring builder product page (Setting or Stone)
    if (isProductPage && productData) {
      const role = productData.dataset.role;
      if (role === 'Setting' || role === 'Stone') {
        return { isInFlow: true, pageType: 'product', role };
      }
    }

    // Expected sapphire collection page
    if (isCollectionPage && context?.sapphireCollectionUrl) {
      const currentPath = window.location.pathname.toLowerCase();
      const expectedPath = context.sapphireCollectionUrl.toLowerCase();
      if (currentPath.includes(expectedPath) || expectedPath.includes(currentPath)) {
        return { isInFlow: true, pageType: 'collection' };
      }
    }

    // Cart is a valid end-state
    if (isCartPage) {
      return { isInFlow: true, pageType: 'cart' };
    }

    return { isInFlow: false, pageType: 'other' };
  }

  /**
   * Clean up stale context when user leaves the ring builder flow
   */
  function cleanupStaleContext() {
    const context = getContext();
    if (!context) return;

    const pageInfo = detectRingBuilderPage();
    if (!pageInfo.isInFlow) {
      console.log('Ring Builder: Clearing stale context - user left flow');
      clearContext();
    }
  }

  /**
   * Save setting context and redirect to sapphire collection (NO cart add yet)
   */
  function addSettingAndRedirect(variantId, redirectUrl, productData) {
    const button = document.querySelector('[data-ring-builder="setting"]');
    if (button) {
      button.classList.add('loading');
      button.setAttribute('disabled', 'disabled');
    }

    // Save context (we'll add both items to cart together when sapphire is selected)
    saveContext({
      settingVariantId: parseInt(variantId, 10),
      settingProductTitle: productData.title,
      settingProductUrl: productData.url,
      settingProductImage: productData.image,
      sapphireCollectionUrl: redirectUrl
    });

    // Redirect to sapphire collection
    window.location.href = redirectUrl;
  }

  /**
   * Add both setting and sapphire to cart with nested relationship, then open cart drawer
   */
  async function addSapphireToSetting(variantId) {
    const context = getContext();
    if (!context) {
      console.error('Ring Builder: No active context found');
      showRingBuilderMessage(
        'Your ring builder session has expired. Please start again by selecting a setting.',
        'error'
      );
      return;
    }

    const button = document.querySelector('[data-ring-builder="sapphire"]');
    if (button) {
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');
    }

    const sapphireVariantId = parseInt(variantId, 10);
    const settingVariantId = context.settingVariantId;
    const cartDrawer = document.querySelector('cart-drawer');

    // Set active element for focus management (matches product-form behavior)
    if (cartDrawer && button) {
      cartDrawer.setActiveElement(button);
    }

    try {
      // Add BOTH items in one request with parent_id for nested relationship
      const response = await fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: settingVariantId,
              quantity: 1
            },
            {
              id: sapphireVariantId,
              quantity: 1,
              parent_id: settingVariantId
            }
          ]
        })
      });

      const cartData = await response.json();

      if (!response.ok) {
        throw new Error(cartData.description || 'Failed to add items to cart');
      }

      // Clear context
      clearContext();

      // Publish cart update event (matches product-form behavior)
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ring-builder', productVariantId: sapphireVariantId });
      }

      // Open cart drawer
      if (cartDrawer) {
        // Remove is-empty class if present
        if (cartDrawer.classList.contains('is-empty')) {
          cartDrawer.classList.remove('is-empty');
        }

        // Fetch updated cart drawer sections
        const sectionsToRender = cartDrawer.getSectionsToRender().map(section => section.id);
        const sectionsUrl = `${window.routes.cart_url}?sections=${sectionsToRender.join(',')}`;

        const sectionsResponse = await fetch(sectionsUrl);
        const sectionsData = await sectionsResponse.json();

        // Update and open the cart drawer
        cartDrawer.renderContents({ sections: sectionsData });
      } else {
        // Fallback to cart page if no drawer
        window.location.href = window.routes.cart_url;
      }

    } catch (error) {
      console.error('Ring Builder: Error adding items to cart', error);
      showRingBuilderMessage(
        'There was an error adding the items to your cart. Please try again.',
        'error'
      );
    } finally {
      // Always reset button state (matches product-form behavior)
      if (button) {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
      }
    }
  }

  /**
   * Initialize progress bar UI
   */
  function renderProgressBar() {
    const context = getContext();
    const progressBar = document.getElementById('ring-builder-progress');

    if (!progressBar) {
      return;
    }

    // Check if we're on a ring builder product page
    const productData = document.getElementById('ring-builder-product-data');
    const productRole = productData ? productData.dataset.role : null;

    // Determine if we should show the progress bar
    const shouldShow = context || productRole === 'Setting';

    if (!shouldShow) {
      progressBar.style.display = 'none';
      return;
    }

    // Show progress bar
    progressBar.style.display = 'block';

    // Get elements
    const settingLink = document.getElementById('ring-builder-setting-link');
    const settingTitleLink = document.getElementById('ring-builder-setting-title-link');
    const settingThumb = document.getElementById('ring-builder-setting-thumb');
    const settingTitle = document.getElementById('ring-builder-setting-title');
    const changeSettingLink = document.getElementById('ring-builder-change-setting');

    // Determine current step and populate accordingly
    if (context) {
      // We have context - user has selected a setting, now choosing sapphire
      if (settingLink) {
        settingLink.href = context.settingProductUrl;
      }
      if (settingTitleLink) {
        settingTitleLink.href = context.settingProductUrl;
      }
      if (settingThumb && context.settingProductImage) {
        settingThumb.src = context.settingProductImage;
        settingThumb.alt = context.settingProductTitle;
        settingThumb.style.display = 'block';
      }
      if (settingTitle) {
        settingTitle.textContent = context.settingProductTitle;
      }

      // Show change setting link on Stone products
      if (changeSettingLink && productRole === 'Stone' && productData) {
        changeSettingLink.href = productData.dataset.collectionUrl;
        changeSettingLink.style.display = 'block';
      } else if (changeSettingLink) {
        changeSettingLink.style.display = 'none';
      }

      // Update steps: Setting completed, Sapphire active
      updateProgressSteps('sapphire');

    } else if (productRole === 'Setting') {
      // No context yet, but we're on a Setting product - Step 1
      if (settingLink && productData) {
        settingLink.href = productData.dataset.productUrl;
      }
      if (settingTitleLink && productData) {
        settingTitleLink.href = productData.dataset.productUrl;
      }
      if (settingThumb && productData && productData.dataset.productImage) {
        settingThumb.src = productData.dataset.productImage;
        settingThumb.alt = productData.dataset.productTitle;
        settingThumb.style.display = 'block';
      }
      if (settingTitle && productData) {
        settingTitle.textContent = productData.dataset.productTitle;
      }

      // Hide change setting link (we're on step 1)
      if (changeSettingLink) {
        changeSettingLink.style.display = 'none';
      }

      // Update steps: Setting active
      updateProgressSteps('setting');
    }
  }

  /**
   * Update progress step active/completed states
   * @param {string} currentStep - 'setting', 'sapphire', or 'complete'
   */
  function updateProgressSteps(currentStep) {
    const stepSetting = document.querySelector('.ring-builder-progress__step--setting');
    const stepSapphire = document.querySelector('.ring-builder-progress__step--sapphire');
    const stepComplete = document.querySelector('.ring-builder-progress__step--complete');

    if (!stepSetting || !stepSapphire || !stepComplete) return;

    // Reset all states
    [stepSetting, stepSapphire, stepComplete].forEach(step => {
      step.classList.remove('ring-builder-progress__step--active', 'ring-builder-progress__step--completed');
    });

    if (currentStep === 'setting') {
      // Step 1 active
      stepSetting.classList.add('ring-builder-progress__step--active');
    } else if (currentStep === 'sapphire') {
      // Step 1 completed, Step 2 active
      stepSetting.classList.add('ring-builder-progress__step--completed');
      stepSapphire.classList.add('ring-builder-progress__step--active');
    } else if (currentStep === 'complete') {
      // Steps 1 & 2 completed, Step 3 active
      stepSetting.classList.add('ring-builder-progress__step--completed');
      stepSapphire.classList.add('ring-builder-progress__step--completed');
      stepComplete.classList.add('ring-builder-progress__step--active');
    }
  }

  /**
   * Initialize sapphire PDP UI when context is active
   */
  function initSapphirePDP() {
    const context = getContext();
    if (!context) return;

    // CRITICAL: Only modify UI on Stone products
    const productData = document.getElementById('ring-builder-product-data');
    if (!productData || productData.dataset.role !== 'Stone') return;

    // Validate that this stone is from the correct collection
    if (!isStoneInValidCollection()) {
      showRingBuilderMessage(
        'This stone is not from the collection linked to your selected setting. ' +
        '<a href="' + context.sapphireCollectionUrl + '">Return to the correct collection</a> or ' +
        '<a href="#" onclick="RingBuilder.clearContext(); location.reload(); return false;">start over</a>.',
        'error'
      );
      return;
    }

    // Show sapphire CTA and hide standard add-to-cart
    const sapphireCTA = document.querySelector('[id^="SapphireButton-"]');
    const standardForm = document.querySelector('product-form');
    const standardButton = standardForm?.querySelector('.product-form__submit[type="submit"]');

    if (sapphireCTA) {
      sapphireCTA.style.display = 'block';

      // Populate setting name
      const settingName = sapphireCTA.querySelector('.ring-builder__setting-name');
      if (settingName) {
        settingName.textContent = context.settingProductTitle;
      }
    }

    if (standardButton) {
      standardButton.style.display = 'none';
    }

    // Also hide dynamic checkout buttons
    const dynamicCheckout = document.querySelector('.product-form__checkout');
    if (dynamicCheckout) {
      dynamicCheckout.style.display = 'none';
    }
  }

  /**
   * Initialize event listeners
   */
  function initEventListeners() {
    // Setting "Add Sapphire" button
    document.addEventListener('click', function(e) {
      const settingButton = e.target.closest('[data-ring-builder="setting"]');
      if (settingButton) {
        e.preventDefault();
        const variantId = settingButton.dataset.variantId;
        const redirectUrl = settingButton.dataset.sapphireCollectionUrl;
        const productData = {
          title: settingButton.dataset.productTitle,
          url: settingButton.dataset.productUrl,
          image: settingButton.dataset.productImage
        };
        addSettingAndRedirect(variantId, redirectUrl, productData);
      }
    });

    // Sapphire "Add Sapphire to Setting" button
    document.addEventListener('click', function(e) {
      const sapphireButton = e.target.closest('[data-ring-builder="sapphire"]');
      if (sapphireButton) {
        e.preventDefault();
        const variantId = sapphireButton.dataset.variantId;
        addSapphireToSetting(variantId);
      }
    });
  }

  /**
   * Initialize the ring builder module
   */
  function init() {
    cleanupStaleContext();
    renderProgressBar();
    initSapphirePDP();
    initEventListeners();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API
  return {
    saveContext,
    getContext,
    clearContext,
    isActive,
    addSettingAndRedirect,
    addSapphireToSetting,
    renderProgressBar,
    init
  };
})();

// Make available globally
window.RingBuilder = RingBuilder;
